import { useEffect, useState } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import {
  FileSelection,
  jsonEditorWidgets,
  Options,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  JsonSchema,
  useEditor,
  Widget,
} from '@sagold/react-json-editor';
import clsx from 'clsx';

export const MetaDataInputField = ({
  jsonSchema,
  file,
  onEditorDataChange,
  setErrors,
  documentMetaDataExtraction,
  dropDownOptions,
}: {
  jsonSchema: JsonSchema;
  file: FileSelection;
  onEditorDataChange: (data: Record<string, unknown>) => void;
  setErrors: (fileId: string, error: JsonError[]) => void;
  documentMetaDataExtraction?: FileUploadMetaDataExtractionDto;
  dropDownOptions: Options;
}) => {
  const [disableEditor, setDisableEditor] = useState(false);

  const [initialData] = useState(() => {
    if (!documentMetaDataExtraction) {
      return {};
    }

    const { key = '', source = 'label' } = documentMetaDataExtraction;
    const { value, isInitial } = file.extractedMetaData || {};
    const extractedValue = value?.toString();

    // If the extraction source is "id" or the extraction is not initial, use the value directly
    if ((source === 'id' || !isInitial) && extractedValue) {
      const index = dropDownOptions.values.indexOf(extractedValue);
      if (index !== -1) {
        onEditorDataChange({ [key]: [extractedValue] });
        return { [key]: [extractedValue] };
      }
      return {};
    }

    // If the extraction source is "label", find the corresponding value from the labels
    if (dropDownOptions && typeof extractedValue === 'string') {
      const index = dropDownOptions.labels.indexOf(extractedValue);

      if (index !== -1) {
        onEditorDataChange({ [key]: [dropDownOptions.values[index]] });
        return { [key]: [dropDownOptions.values[index]] };
      }
    }
    return {};
  });

  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: jsonSchema,
    onChange: (_data, _root, editor) => {
      setErrors(file.id, editor.getErrors());
      onEditorDataChange(editor.getData());
    },
    validate: true,
    data: initialData,
    disabled: disableEditor,
  });

  useEffect(() => {
    if (file.uploadState === 'isSuccess') {
      setDisableEditor(true);
    }
  }, [file.uploadState]);

  // Push initial editor errors to the parent prop when the page loads for the first time,
  // so it ensures meta data extraction errors are displayed in the top bar.
  // The following sending of errors are handled on the editors onChange callback.
  useEffect(() => {
    if (editor) {
      const editorErrors = editor.getErrors();

      if (Array.isArray(editorErrors) && editorErrors.length > 0) {
        setErrors(file.id, editorErrors);
      }
    }
  }, [editor, file.id, setErrors]);

  return (
    <div className={clsx('portal-form rje-form rje-theme rje-theme--light')}>
      <Widget editor={editor} />
    </div>
  );
};
